/**
 * MappingActivityLog Component
 *
 * Displays user-mapping activity (created, updated, deleted, reassigned)
 * with filter chips, cascade impact at row level, and richer diff rendering.
 */

import { useMemo, useState } from 'react';
import {
  Users,
  ChevronDown,
  ChevronRight,
  Download,
  RefreshCw,
  CheckCircle2,
  User as UserIcon,
  FileSpreadsheet,
  Plus,
  Minus,
  ArrowRight,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { EmptyState } from '@/components/EmptyState';
import { toast } from '@/lib/toast';
import { cn, formatTimestamp, formatRelativeTime } from '@/lib/utils';
import { useMappingHistory } from '@/hooks/useActivityLogs';
import { useTriggerSync } from '@/hooks/useSync';
import type { MappingHistoryEntry } from '@/types/domain';

interface MappingActivityLogProps {
  projectId: string;
}

type ActionKey = MappingHistoryEntry['action'];

const actionConfig: Record<string, {
  label: string;
  badgeClass: string;
  borderClass: string;
}> = {
  created:          { label: 'Created',     badgeClass: 'bg-success/10 text-success border-success/30',         borderClass: 'border-l-success' },
  updated:          { label: 'Updated',     badgeClass: 'bg-info/10 text-info border-info/30',                  borderClass: 'border-l-info' },
  deleted:          { label: 'Deleted',     badgeClass: 'bg-destructive/10 text-destructive border-destructive/30', borderClass: 'border-l-destructive' },
  synced:           { label: 'Synced',      badgeClass: 'bg-muted text-muted-foreground border-border',         borderClass: 'border-l-muted-foreground/30' },
  renamed:          { label: 'Renamed',     badgeClass: 'bg-info/10 text-info border-info/30',                  borderClass: 'border-l-info' },
  reassigned:       { label: 'Reassigned',  badgeClass: 'bg-warning/10 text-warning border-warning/30',         borderClass: 'border-l-warning' },
  unmatched_report: { label: 'Unmatched',   badgeClass: 'bg-warning/10 text-warning border-warning/30',         borderClass: 'border-l-warning' },
};

const FILTER_ORDER: ActionKey[] = [
  'reassigned', 'unmatched_report', 'deleted', 'updated', 'created', 'renamed', 'synced',
];

const sourceLabels: Record<string, string> = {
  mapping_sync: 'Mapping Sync',
  mappings_create: 'Manual',
  mappings_update: 'Manual',
  mappings_delete: 'Manual',
  projects_create: 'Excel import',
  excel_import: 'Excel import',
  scenario_sync: 'Scenario Sync',
  scenario_sync_sweep: 'Scenario Sync',
};

function classifySource(source: string): 'sync' | 'excel' | 'manual' {
  if (source === 'mapping_sync' || source === 'scenario_sync' || source === 'scenario_sync_sweep') {
    return 'sync';
  }
  if (source === 'excel_import' || source === 'projects_create') {
    return 'excel';
  }
  return 'manual';
}

function parseMappingSk(sk: string): { subdomain: string; area: string } | null {
  // Format: "ROLE#subdomain#area"
  const parts = sk.split('#');
  if (parts.length >= 3) {
    return { subdomain: parts[1], area: parts[2] };
  }
  return null;
}

function formatValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return '';
  if (Array.isArray(value)) return value.join(', ');
  return String(value);
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function ValueChip({ value }: { value: string }) {
  if (!value) {
    return <span className="text-muted-foreground italic">empty</span>;
  }
  // For UUIDs, show first 8 chars with tooltip for full value
  if (UUID_RE.test(value)) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="font-mono tabular-nums text-foreground cursor-help">
              {value.slice(0, 8)}…
            </span>
          </TooltipTrigger>
          <TooltipContent>
            <span className="font-mono">{value}</span>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }
  // For long strings, truncate visually but keep full in title
  if (value.length > 60) {
    return (
      <span className="truncate text-foreground" title={value}>
        {value}
      </span>
    );
  }
  return <span className="text-foreground">{value}</span>;
}

type DiffKind = 'modified' | 'added' | 'removed';
interface DiffField {
  kind: DiffKind;
  field: string;
  from: string;
  to: string;
}

function computeDiff(entry: MappingHistoryEntry): DiffField[] {
  const result: DiffField[] = [];
  const old = entry.old_values ?? {};
  const next = entry.new_values ?? {};

  if (entry.action === 'deleted') {
    for (const [k, v] of Object.entries(old)) {
      result.push({ kind: 'removed', field: k, from: formatValue(v), to: '' });
    }
    return result;
  }

  const allKeys = new Set([...Object.keys(old), ...Object.keys(next)]);
  for (const key of allKeys) {
    const fromV = formatValue(old[key]);
    const toV = formatValue(next[key]);
    if (fromV === toV) continue;
    if (!fromV && toV) {
      result.push({ kind: 'added', field: key, from: '', to: toV });
    } else if (fromV && !toV) {
      result.push({ kind: 'removed', field: key, from: fromV, to: '' });
    } else {
      result.push({ kind: 'modified', field: key, from: fromV, to: toV });
    }
  }
  return result;
}

function CascadePill({ created, deleted }: { created: number; deleted: number }) {
  if (!created && !deleted) {
    return <span className="text-muted-foreground/50">—</span>;
  }
  return (
    <div className="flex items-center gap-1.5">
      {created > 0 && (
        <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-xs font-medium tabular-nums bg-success/10 text-success">
          <Plus className="h-3 w-3" />{created}
        </span>
      )}
      {deleted > 0 && (
        <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-xs font-medium tabular-nums bg-destructive/10 text-destructive">
          <Minus className="h-3 w-3" />{deleted}
        </span>
      )}
    </div>
  );
}

function TriggerCell({ entry }: { entry: MappingHistoryEntry }) {
  const kind = classifySource(entry.source);
  const Icon = kind === 'sync' ? RefreshCw : kind === 'excel' ? FileSpreadsheet : UserIcon;
  const label = kind === 'manual'
    ? entry.changed_by_name
    : (sourceLabels[entry.source] ?? entry.source);
  return (
    <div className="flex items-center gap-1.5 text-sm">
      <Icon className={cn(
        'h-3.5 w-3.5 shrink-0',
        kind === 'sync' && 'text-info',
        kind === 'excel' && 'text-success',
        kind === 'manual' && 'text-muted-foreground',
      )} />
      <span className="truncate">{label}</span>
    </div>
  );
}

function MappingCell({ entry }: { entry: MappingHistoryEntry }) {
  const isUnmatchedReport = entry.action === 'unmatched_report';
  const unresolvedCount = isUnmatchedReport
    ? ((entry.new_values as Record<string, unknown>)?.count as number | undefined)
    : undefined;
  if (isUnmatchedReport && unresolvedCount !== undefined) {
    const parsed = parseMappingSk(entry.mapping_sk);
    if (!parsed) {
      return (
        <span className="text-sm text-muted-foreground">
          {unresolvedCount} unmatched combo{unresolvedCount !== 1 ? 's' : ''}
        </span>
      );
    }
  }
  const parsed = parseMappingSk(entry.mapping_sk);
  if (!parsed) {
    return <span className="text-sm">{entry.mapping_sk.replace(/^ROLE#/, '')}</span>;
  }
  return (
    <div className="flex items-center gap-1.5 text-sm">
      <span className="text-muted-foreground">{parsed.subdomain}</span>
      <ChevronRight className="h-3 w-3 text-muted-foreground/60" />
      <span className="font-medium text-foreground">{parsed.area}</span>
    </div>
  );
}

function MappingLogRow({ entry }: { entry: MappingHistoryEntry }) {
  const [expanded, setExpanded] = useState(false);
  const config = actionConfig[entry.action] ?? actionConfig.created;
  const isUnmatchedReport = entry.action === 'unmatched_report';

  const diff = computeDiff(entry);
  const cascade = entry.cascade_stats ?? { test_cases_created: 0, test_cases_deleted: 0 };

  const unmatchedCombos = isUnmatchedReport
    ? ((entry.new_values as Record<string, unknown>)?.unmatched_combos as string[] ?? [])
    : [];
  const unresolvedPersons = isUnmatchedReport
    ? ((entry.new_values as Record<string, unknown>)?.unresolved_persons as string[] ?? [])
    : [];

  const hasDetails =
    diff.length > 0 ||
    unmatchedCombos.length > 0 ||
    unresolvedPersons.length > 0;

  // For 'created' actions, group all "added" diffs into a compact summary
  const isPureCreation = entry.action === 'created' && diff.every(d => d.kind === 'added');
  const modifiedDiffs = diff.filter(d => d.kind === 'modified');
  const addedDiffs = diff.filter(d => d.kind === 'added');
  const removedDiffs = diff.filter(d => d.kind === 'removed');

  return (
    <>
      <TableRow
        className={cn(
          'border-l-2',
          config.borderClass,
          hasDetails && 'cursor-pointer hover:bg-accent/5',
        )}
        onClick={hasDetails ? () => setExpanded(!expanded) : undefined}
        role={hasDetails ? 'button' : undefined}
        tabIndex={hasDetails ? 0 : undefined}
        aria-expanded={hasDetails ? expanded : undefined}
        onKeyDown={hasDetails ? (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setExpanded(!expanded);
          }
        } : undefined}
      >
        <TableCell className="align-middle">
          <div className="flex items-center gap-1.5">
            {hasDetails ? (
              expanded
                ? <ChevronDown className="h-3 w-3 text-muted-foreground shrink-0" />
                : <ChevronRight className="h-3 w-3 text-muted-foreground shrink-0" />
            ) : (
              <span className="w-3 shrink-0" />
            )}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="leading-tight">
                    <div className="text-sm tabular-nums">{formatTimestamp(entry.changed_at)}</div>
                    <div className="text-[11px] text-muted-foreground tabular-nums">
                      {formatRelativeTime(entry.changed_at)}
                    </div>
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  {new Date(entry.changed_at).toLocaleString()}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </TableCell>
        <TableCell className="align-middle">
          <Badge variant="outline" className={cn('text-xs font-medium', config.badgeClass)}>
            {config.label}
          </Badge>
        </TableCell>
        <TableCell className="align-middle">
          <MappingCell entry={entry} />
        </TableCell>
        <TableCell className="align-middle">
          <TriggerCell entry={entry} />
        </TableCell>
        <TableCell className="align-middle text-right pr-3">
          <CascadePill
            created={cascade.test_cases_created}
            deleted={cascade.test_cases_deleted}
          />
        </TableCell>
      </TableRow>

      {expanded && hasDetails && (
        <tr>
          <td colSpan={5} className="p-0">
            <div className={cn(
              'px-6 py-3 border-t border-l-2 space-y-3',
              config.borderClass,
              isUnmatchedReport ? 'bg-warning/5' : 'bg-muted/30',
            )}>
              {/* Pure-creation summary: compact "Initial values" view */}
              {isPureCreation && addedDiffs.length > 0 && (
                <div className="space-y-1">
                  <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    Initial values
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1">
                    {addedDiffs.map(({ field, to }) => (
                      <div key={field} className="flex items-baseline gap-2 text-sm min-w-0">
                        <span className="font-mono text-xs text-muted-foreground shrink-0">
                          {field}
                        </span>
                        <span className="min-w-0 truncate">
                          <ValueChip value={to} />
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Modified fields */}
              {!isPureCreation && modifiedDiffs.length > 0 && (
                <div className="space-y-1">
                  <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    Modified
                  </div>
                  {modifiedDiffs.map(({ field, from, to }) => (
                    <div key={field} className="flex items-baseline gap-2 text-sm min-w-0">
                      <Badge variant="outline" className="text-[10px] font-mono shrink-0 px-1.5 py-0 h-5">
                        {field}
                      </Badge>
                      <span className="min-w-0 truncate"><ValueChip value={from} /></span>
                      <ArrowRight className="h-3 w-3 text-muted-foreground shrink-0" />
                      <span className="min-w-0 truncate font-medium"><ValueChip value={to} /></span>
                    </div>
                  ))}
                </div>
              )}

              {/* Added fields (only when mixed with other diff types) */}
              {!isPureCreation && addedDiffs.length > 0 && (
                <div className="space-y-1">
                  <div className="text-xs font-medium text-success uppercase tracking-wide flex items-center gap-1">
                    <Plus className="h-3 w-3" /> Added
                  </div>
                  {addedDiffs.map(({ field, to }) => (
                    <div key={field} className="flex items-baseline gap-2 text-sm min-w-0">
                      <Badge variant="outline" className="text-[10px] font-mono shrink-0 px-1.5 py-0 h-5">
                        {field}
                      </Badge>
                      <span className="min-w-0 truncate"><ValueChip value={to} /></span>
                    </div>
                  ))}
                </div>
              )}

              {/* Removed fields */}
              {removedDiffs.length > 0 && (
                <div className="space-y-1">
                  <div className="text-xs font-medium text-destructive uppercase tracking-wide flex items-center gap-1">
                    <Minus className="h-3 w-3" /> Removed
                  </div>
                  {removedDiffs.map(({ field, from }) => (
                    <div key={field} className="flex items-baseline gap-2 text-sm min-w-0">
                      <Badge variant="outline" className="text-[10px] font-mono shrink-0 px-1.5 py-0 h-5">
                        {field}
                      </Badge>
                      <span className="min-w-0 truncate line-through text-muted-foreground">
                        <ValueChip value={from} />
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {unmatchedCombos.length > 0 && (
                <div className="space-y-1">
                  <div className="text-xs font-medium text-warning uppercase tracking-wide">
                    Unmatched tester / area combos
                  </div>
                  <ul className="list-disc list-inside text-sm text-foreground/80 space-y-0.5">
                    {unmatchedCombos.map((combo) => (
                      <li key={combo} className="font-mono text-xs">{combo}</li>
                    ))}
                  </ul>
                </div>
              )}

              {unresolvedPersons.length > 0 && (
                <div className="space-y-1">
                  <div className="text-xs font-medium text-warning uppercase tracking-wide">
                    Persons not matched to a Cognito user
                  </div>
                  <p className="text-xs text-muted-foreground">
                    No test cases were created for these persons.
                  </p>
                  <ul className="list-disc list-inside text-sm text-foreground/80 space-y-0.5">
                    {unresolvedPersons.map((p) => (
                      <li key={p}>{p}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

export function MappingActivityLog({ projectId }: MappingActivityLogProps) {
  const { data, isLoading } = useMappingHistory(projectId, { refetchInterval: 30000 });
  const triggerSync = useTriggerSync(projectId);
  const [showAll, setShowAll] = useState(false);
  const [syncSuccess, setSyncSuccess] = useState(false);
  const [activeFilter, setActiveFilter] = useState<ActionKey | 'all'>('all');

  const handleSync = () => {
    const promise = triggerSync.mutateAsync(undefined);
    toast.promise(promise, {
      loading: 'Syncing mappings from SharePoint...',
      success: () => {
        setSyncSuccess(true);
        setTimeout(() => setSyncSuccess(false), 3000);
        return 'Mapping sync completed';
      },
      error: (err: Error) => `Sync failed: ${err.message}`,
    });
  };

  const entries = useMemo(() => data?.history ?? [], [data?.history]);

  const filterCounts = useMemo(() => {
    const counts = new Map<ActionKey, number>();
    for (const e of entries) {
      counts.set(e.action, (counts.get(e.action) ?? 0) + 1);
    }
    return counts;
  }, [entries]);

  const filteredEntries = useMemo(() => {
    if (activeFilter === 'all') return entries;
    return entries.filter(e => e.action === activeFilter);
  }, [entries, activeFilter]);

  const handleExport = () => {
    const csv = [
      'Timestamp,Action,Mapping,Trigger,Source,Test Cases Created,Test Cases Deleted,Changes',
      ...entries.map(entry => {
        const parsed = parseMappingSk(entry.mapping_sk);
        const mapping = parsed ? `${parsed.subdomain} / ${parsed.area}` : entry.mapping_sk.replace(/^ROLE#/, '');
        const triggerKind = classifySource(entry.source);
        const triggerLabel = triggerKind === 'manual'
          ? entry.changed_by_name
          : (sourceLabels[entry.source] ?? entry.source);
        const cascade = entry.cascade_stats ?? { test_cases_created: 0, test_cases_deleted: 0 };
        const diff = computeDiff(entry);
        const changes = diff.map(d =>
          d.kind === 'added' ? `+${d.field}=${d.to}`
          : d.kind === 'removed' ? `-${d.field}=${d.from}`
          : `${d.field}: ${d.from} → ${d.to}`
        ).join('; ');
        return [
          new Date(entry.changed_at).toISOString(),
          entry.action,
          mapping,
          triggerLabel,
          entry.source,
          cascade.test_cases_created,
          cascade.test_cases_deleted,
          changes,
        ].map(v => `"${String(v).replace(/"/g, '""')}"`).join(',');
      }),
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `mapping-activity-${projectId}-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Mapping activity exported to CSV');
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Mapping Activity</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </CardContent>
      </Card>
    );
  }

  if (entries.length === 0) {
    return (
      <EmptyState
        icon={Users}
        title="No Mapping Activity"
        description="Mapping activity will appear here when user mappings are created, updated, or deleted."
        action={{
          label: 'Sync Mappings',
          onClick: handleSync,
        }}
      />
    );
  }

  const visibleFilters: { key: ActionKey | 'all'; label: string; count: number }[] = [
    { key: 'all', label: 'All', count: entries.length },
    ...FILTER_ORDER
      .map(key => ({
        key,
        label: actionConfig[key]?.label ?? key,
        count: filterCounts.get(key) ?? 0,
      }))
      .filter(f => f.count > 0),
  ];

  const displayEntries = showAll ? filteredEntries : filteredEntries.slice(0, 5);

  return (
    <Card>
      <CardHeader className="space-y-3">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="h-5 w-5 text-primary" />
              Mapping Activity
            </CardTitle>
            <CardDescription>
              {entries.length} mapping event{entries.length !== 1 ? 's' : ''}
              {filteredEntries.length !== entries.length && (
                <> · showing {filteredEntries.length} {actionConfig[activeFilter as ActionKey]?.label.toLowerCase() ?? activeFilter}</>
              )}
            </CardDescription>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleExport}
              className="gap-2"
            >
              <Download className="h-4 w-4" />
              Export
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleSync}
              disabled={triggerSync.isPending}
              className="gap-2"
            >
              {syncSuccess ? (
                <CheckCircle2 className="h-4 w-4 text-success" />
              ) : (
                <RefreshCw className={cn(
                  'h-4 w-4',
                  triggerSync.isPending && 'animate-spin',
                )} />
              )}
              {triggerSync.isPending ? 'Syncing...' : syncSuccess ? 'Done' : 'Sync Now'}
            </Button>
          </div>
        </div>

        {visibleFilters.length > 2 && (
          <div className="flex items-center gap-1 flex-wrap">
            {visibleFilters.map(f => {
              const isActive = activeFilter === f.key;
              const cfg = f.key === 'all' ? null : actionConfig[f.key];
              return (
                <button
                  key={f.key}
                  type="button"
                  onClick={() => {
                    setActiveFilter(f.key);
                    setShowAll(false);
                  }}
                  className={cn(
                    'inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-medium border transition-colors',
                    isActive
                      ? cfg
                        ? cfg.badgeClass
                        : 'bg-foreground text-background border-foreground'
                      : 'bg-transparent text-muted-foreground border-border hover:bg-muted',
                  )}
                >
                  <span>{f.label}</span>
                  <span className={cn(
                    'tabular-nums',
                    isActive ? 'opacity-90' : 'opacity-60',
                  )}>
                    {f.count}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[150px] pl-3">Time</TableHead>
              <TableHead className="w-[110px]">Action</TableHead>
              <TableHead>Mapping</TableHead>
              <TableHead className="w-[180px]">Trigger</TableHead>
              <TableHead className="w-[120px] text-right pr-3">Impact</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {displayEntries.map((entry, idx) => (
              <MappingLogRow key={`${entry.changed_at}-${idx}`} entry={entry} />
            ))}
          </TableBody>
        </Table>
        {filteredEntries.length === 0 && (
          <div className="py-8 text-center text-sm text-muted-foreground">
            No events match this filter.
          </div>
        )}
        {filteredEntries.length > 5 && (
          <div className="mt-3 text-center">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowAll(!showAll)}
              className="text-muted-foreground"
            >
              {showAll ? 'Show less' : `Show all ${filteredEntries.length} entries`}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
